import React, { useRef, useState } from "react";
import { CONTENT_REVIEW_STATUS } from "@pages/campaigns/content-review/statuses";

import ContentRejectedAlert from "@pages/profile/content-review/ContentRejectedAlert";
import BlankState from "@components/BlankState";
import ResultsGrid from "@components/ResultsGrid";
import UploadedContentHeader from "./UploadedContentHeader";
import ContentCaption from "./ContentCaption";
import { sortBy } from "lodash";
import { ContentReviewDeliverable, ParticipantContentReview } from "@types";

import { getDeliverableStatus, getStatusBadge } from "@utils/CampaignUtils";
import FButton from "@components/FButton";
import BriefDeliverablePreviewModal from "../../brief-preview/BriefDeliverablePreview";
import DeliverableInfo from "./components/DeliverableInfo";
import ButtonSubmitForReview from "./components/ButtonSubmitForReview";

interface DeliverableContentSectionProps {
  deliverables: ContentReviewDeliverable[];
  selectedRounds: Record<number, number>;
  onRoundChange: (deliverableId: number, value: number) => void;
  onSubmitForReview: (content: any) => void;
  submittingForReviewDeliverableId: number;
  onUploadContentClick: (deliverable: any, uploadAssetsOnly?: boolean) => void;
  onDeleteAsset: (
    assetId: number,
    roundId: number,
    deliverableId: number
  ) => void;
  onAssetClick: (asset: any) => void;
  campaignParticipant?: ParticipantContentReview;
}

const DeliverableContentSection: React.FC<DeliverableContentSectionProps> = ({
  deliverables,
  selectedRounds,
  onRoundChange,
  onSubmitForReview,
  submittingForReviewDeliverableId,
  onUploadContentClick,
  onDeleteAsset,
  onAssetClick,
  campaignParticipant = {},
}) => {
  // Create refs for each deliverable section
  const sectionRefs = deliverables.reduce((acc, deliverable) => {
    acc[deliverable.id] = useRef<HTMLDivElement>(null);
    return acc;
  }, {});

  // State to track which deliverable's requirements modal is open
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);

  const scrollToSection = (deliverableId) => {
    const ref = sectionRefs[deliverableId];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="flex flex-col gap-6 relative">
      <h2 className="text-xl font-semibold mb-0" id="your-deliverables-title">
        Your Deliverables ({deliverables.length})
      </h2>
      {/* Deliverable Status List - Single Container */}
      <div className="mb-8 bg-white overflow-hidden border border-highlight_red rounded-2xl shadow-lg">
        {deliverables.map((deliverable, index) => {
          const status = getDeliverableStatus(deliverable);
          const isLastItem = index === deliverables.length - 1;

          return (
            <div
              key={`status-${deliverable.id}`}
              className={`w-full bg-white hover:bg-gray-50 cursor-pointer border-bottom border-highlight_red  justify-between transition-all duration-200 ${!isLastItem ? "border-b border-highlight_red" : ""}`}
              onClick={() => scrollToSection(deliverable.id)}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center py-4 px-4 sm:px-6 w-full gap-3 sm:gap-0">
                <DeliverableInfo deliverable={deliverable} />
                <div className="ml-auto w-auto flex items-center justify-end hidden w-full sm:inline-flex">
                  {status === CONTENT_REVIEW_STATUS.NOT_SUBMITTED &&
                  onSubmitForReview ? (
                    <ButtonSubmitForReview
                      onSubmitForReview={() =>
                        onSubmitForReview(deliverable.content)
                      }
                      isLoading={
                        submittingForReviewDeliverableId === deliverable.id
                      }
                    />
                  ) : (
                    getStatusBadge(status)
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {deliverables.map((deliverable) => {
        const { content } = deliverable;

        const status = getDeliverableStatus(deliverable);

        // Create a section for each deliverable
        return (
          <div
            ref={sectionRefs[deliverable.id]}
            id={`deliverable-${deliverable.id}`}
            key={`content-${deliverable.id}`}
            className="overflow-hidden rounded-2xl shadow-lg mb-16"
          >
            <div className="bg-white flex flex-col sm:flex-row items-start sm:items-center px-4 sm:px-8 py-4 sm:py-6 border-b border-highlight_red w-full shadow-md">
              <DeliverableInfo deliverable={deliverable} />
              <div className="flex items-center gap-3 sm:gap-4 ml-0 sm:ml-auto mt-4 sm:mt-0 w-full sm:w-auto justify-between sm:justify-end">
                <div className="ml-auto w-auto flex items-center justify-end hidden w-full sm:inline-flex">
                  {getStatusBadge(status)}
                </div>
                <FButton
                  onClick={() => setSelectedDeliverable(deliverable)}
                  label="View Requirements"
                  iconLeft={{
                    name: "eye",
                    size: 16,
                    color: "#000F45",
                    className: "mr-1 -mt-1",
                  }}
                  className="border border-default_weak text-sm whitespace-nowrap"
                />
              </div>
            </div>
            {!content ? (
              <BlankState
                title="No Content Yet"
                className="mt-0 !rounded-none"
                subtitle="Upload content that meets the requirements to complete this deliverable."
                icon={"warning-2"}
                onActionClick={() => onUploadContentClick(deliverable)}
                actionLabel="Upload content"
              />
            ) : (
              <>
                {(() => {
                  const {
                    rounds: unorderedRounds = [],
                    status: contentStatus,
                  } = content;
                  const rounds = sortBy(unorderedRounds, "stage").reverse();

                  const selectedRoundIndex =
                    selectedRounds[deliverable.id] || 0;

                  const round = rounds[selectedRoundIndex];
                  const { assets = [] } = round || {};

                  const isOldRound =
                    rounds.length > 1 && selectedRoundIndex > 0;

                  const status = isOldRound
                    ? CONTENT_REVIEW_STATUS.INFLUENCER_CHANGES
                    : contentStatus;

                  const isRejected =
                    status === CONTENT_REVIEW_STATUS.INFLUENCER_CHANGES;

                  const isEditable = ![
                    CONTENT_REVIEW_STATUS.APPROVED,
                    CONTENT_REVIEW_STATUS.ADMIN_CHANGES,
                    CONTENT_REVIEW_STATUS.INFLUENCER_CHANGES,
                  ].includes(status as CONTENT_REVIEW_STATUS);

                  // Handle asset deletion with proper arguments
                  const handleLocalAssetDelete = (assetId: number) => {
                    if (round?.id) {
                      onDeleteAsset(assetId, round.id, deliverable.id);
                    }
                  };

                  return (
                    <div className="p-4 sm:p-8 bg-[rgba(255,255,255,0.5)]">
                      {isRejected && (
                        <ContentRejectedAlert
                          round={round}
                          onAction={
                            isOldRound
                              ? null
                              : () => onUploadContentClick(deliverable)
                          }
                        />
                      )}
                      {rounds.length > 0 && (
                        <div>
                          <UploadedContentHeader
                            rounds={rounds}
                            selectedRound={selectedRoundIndex}
                            onRoundChange={(value) =>
                              onRoundChange(deliverable.id, value)
                            }
                            deliverable={deliverable}
                            onAddNew={
                              isEditable
                                ? () => {
                                    onUploadContentClick(
                                      deliverable,
                                      !!deliverable.content.caption
                                    );
                                  }
                                : undefined
                            }
                          />
                          <ContentCaption
                            caption={deliverable?.content?.caption || ""}
                            isEditable={isEditable}
                            onClick={() => {
                              onUploadContentClick(deliverable, false);
                            }}
                          />
                        </div>
                      )}
                      <ResultsGrid
                        cardData={assets.map((asset) => ({
                          ...content,
                          id: asset.id,
                          deliverable,
                          participant: {},
                          campaignParticipant,
                          rounds: [{ assets: [asset], id: round?.id }],
                        }))}
                        cardType="contentReview"
                        onDelete={
                          status === CONTENT_REVIEW_STATUS.NOT_SUBMITTED
                            ? handleLocalAssetDelete
                            : null
                        }
                        onCardClick={onAssetClick}
                        addNewLabel={
                          assets.length > 0 ? "Upload more" : "Upload content"
                        }
                      />

                      {/* Submit button footer for each deliverable */}
                      {status === CONTENT_REVIEW_STATUS.NOT_SUBMITTED &&
                        assets.length > 0 &&
                        onSubmitForReview && (
                          <div className="mt-8 w-full flex pt-8 justify-end border-t border-highlight_red">
                            <ButtonSubmitForReview
                              onSubmitForReview={() =>
                                onSubmitForReview(content)
                              }
                              isLoading={
                                submittingForReviewDeliverableId ===
                                deliverable.id
                              }
                            />
                          </div>
                        )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        );
      })}
      {/* Deliverable Requirements Modal */}
      {selectedDeliverable && (
        <BriefDeliverablePreviewModal
          deliverable={selectedDeliverable}
          onClose={() => setSelectedDeliverable(null)}
        />
      )}
    </div>
  );
};

export default DeliverableContentSection;
